import React, { useState, useEffect } from 'react';
import {
  Container,
  Box,
  Card,
  CardContent,
  Button,
  Typography,
  Alert,
  Divider,
  Stack,
  CircularProgress,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { Smartphone, Download } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { getDownloadURL, ref } from 'firebase/storage';
import { storage } from '../firebase/config';
import { useTheme } from '../context/ThemeContext';

// Path inside the Storage bucket. Keep the filename stable across releases so
// this page never needs to change when a new build ships.
const APK_PATH = 'mobile/pakettihuolto-mobile-beta.apk';
const APP_VERSION = '1.0.0';

const STEP_KEYS = ['download', 'install', 'playProtect', 'login', 'deliveries'];

const detectPlatform = () => {
  if (typeof navigator === 'undefined') return 'desktop';
  const ua = navigator.userAgent || '';
  if (/android/i.test(ua)) return 'android';
  if (/iPad|iPhone|iPod/.test(ua)) return 'ios';
  return 'desktop';
};

const Step = ({ number, title, body }) => (
  <Stack direction="row" spacing={2} alignItems="flex-start">
    <Box
      sx={{
        flexShrink: 0,
        width: 30,
        height: 30,
        borderRadius: '10px',
        bgcolor: 'primary.main',
        color: '#fff',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontWeight: 700,
        fontSize: 14,
      }}
    >
      {number}
    </Box>
    <Box>
      <Typography variant="subtitle2" fontWeight={600} color="text.primary">
        {title}
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
        {body}
      </Typography>
    </Box>
  </Stack>
);

const DownloadApp = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { isDark } = useTheme();

  const [downloadUrl, setDownloadUrl] = useState(null);
  const [error, setError] = useState('');
  const [platform] = useState(detectPlatform);

  useEffect(() => {
    let cancelled = false;

    getDownloadURL(ref(storage, APK_PATH))
      .then((url) => {
        if (!cancelled) setDownloadUrl(url);
      })
      .catch(() => {
        if (!cancelled) setError(t('downloadApp.error.urlFailed'));
      });

    return () => {
      cancelled = true;
    };
  }, [t]);

  const loading = !downloadUrl && !error;

  return (
    <Box
      sx={{
        bgcolor: 'background.default',
        minHeight: '100vh',
        marginTop: 8,
        display: 'flex',
        alignItems: 'center',
        pt: 4,
        pb: 6,
      }}
    >
      <Container maxWidth="sm">
        {/* Brand header */}
        <Box sx={{ textAlign: 'center', mb: 4 }}>
          <Box
            sx={{
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: 52,
              height: 52,
              borderRadius: '14px',
              bgcolor: 'primary.main',
              boxShadow: isDark
                ? '0 0 0 8px rgba(34,211,238,0.08)'
                : '0 0 0 8px rgba(8,145,178,0.08)',
              mb: 2.5,
            }}
          >
            <Smartphone size={26} color="#fff" />
          </Box>
          <Typography variant="h5" fontWeight={700} color="text.primary">
            {t('downloadApp.title')}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            {t('downloadApp.subtitle')}
          </Typography>
        </Box>

        <Card>
          <CardContent sx={{ p: 4 }}>
            {error && (
              <Alert severity="error" sx={{ mb: 3 }}>
                {error}
              </Alert>
            )}

            {platform === 'ios' && (
              <Alert severity="info" sx={{ mb: 3 }}>
                {t('downloadApp.notice.ios')}
              </Alert>
            )}

            {platform === 'desktop' && (
              <Alert severity="info" sx={{ mb: 3 }}>
                {t('downloadApp.notice.desktop')}
              </Alert>
            )}

            <Button
              component="a"
              href={downloadUrl || undefined}
              download
              variant="contained"
              fullWidth
              size="large"
              disabled={loading || Boolean(error)}
              startIcon={
                loading ? <CircularProgress size={18} color="inherit" /> : <Download size={18} />
              }
            >
              {loading ? t('downloadApp.preparing') : t('downloadApp.download')}
            </Button>

            <Typography
              variant="caption"
              color="text.secondary"
              display="block"
              textAlign="center"
              sx={{ mt: 1.5 }}
            >
              {t('downloadApp.versionInfo', { version: APP_VERSION })}
            </Typography>

            <Divider sx={{ my: 3 }} />

            <Typography variant="subtitle1" fontWeight={700} color="text.primary" sx={{ mb: 2.5 }}>
              {t('downloadApp.stepsTitle')}
            </Typography>

            <Stack spacing={2.5}>
              {STEP_KEYS.map((key, i) => (
                <Step
                  key={key}
                  number={i + 1}
                  title={t(`downloadApp.steps.${key}.title`)}
                  body={t(`downloadApp.steps.${key}.body`)}
                />
              ))}
            </Stack>

            <Alert severity="warning" sx={{ mt: 3 }}>
              {t('downloadApp.unknownSources')}
            </Alert>

            <Typography
              variant="body2"
              color="text.secondary"
              textAlign="center"
              sx={{ mt: 3 }}
            >
              {t('downloadApp.support')}
            </Typography>
          </CardContent>
        </Card>

        <Box sx={{ textAlign: 'center', mt: 3 }}>
          <Button variant="text" onClick={() => navigate('/')} color="inherit">
            ← {t('auth.backToHome')}
          </Button>
        </Box>
      </Container>
    </Box>
  );
};

export default DownloadApp;